import React from "react";
import { ReactNode } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { Box, Button, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { LoadPanel } from "devextreme-react";

import { IDateUtil, IHttpClient } from "@ezhou/common";
import { DateFnsDateUtil } from "@ezhou/common-utils";
import { EzhouForm, PageProps } from "@ezhou/core";

import { getUserProfileFromCache } from "../../services/userSettingService";

import {
  ExportingByPostingDateSteps,
  ExportingStep,
  getExportingStepLabels,
} from "./model/exportingSteps";
import { JeExportByPostingDateModel } from "./model/jeExport";
import {
  GenerateTransactionJournalEntriesRequest,
  GenerateTransactionJournalEntriesResponse,
  GetJournalEntryExportResponse,
  GetJournalEntryExportResultsRequest,
  GroupJournalEntriesRequest,
  GroupJournalEntriesResponse,
  GetTessituraTransactionsByPostingDateRequest,
  GetTessituraTransactionsByPostingDateResponse,
} from "./model/modelData";
import { UnderConstructionComponent } from "./UnderConstruction";
import { SelectPostingDate } from "./SelectPostingDate";
import { ReviewRawTransactions } from "./ReviewRawTransactions";
import { ReviewTransactionLevelJournalEntries } from "./ReviewTransactionLevelJournalEntries";
import { ReviewGroupedJournalEntries } from "./ReviewGroupedJournalEntries";
import { ReviewJournalEntryExports } from "./ReviewJournalEntryExports";
import { ReviewExportConfirmation } from "./ReviewExportConfirmation";

type PageState = {
  steps: string[];
  activeStep: number;
  processing: boolean;
};

type CreationStepHandledResult = {
  success: boolean;
  message?: string;
};

type JeExportByPostingPageProps = PageProps & {
  navigate: NavigateFunction;
};

class JeExportByPostingDate extends EzhouForm<JeExportByPostingPageProps, PageState> {
  navigate: NavigateFunction;
  httpClient: IHttpClient;
  skipped: Set<number>;
  steps: ExportingStep[];
  jeExport: JeExportByPostingDateModel;

  constructor(props: JeExportByPostingPageProps) {
    super(props);

    this.navigate = props.navigate;
    this.httpClient = this.pageProps.appUtil.httpClients["eAccountingService"];
    this.skipped = new Set<number>();
    this.steps = ExportingByPostingDateSteps;
    this.state = {
      steps: getExportingStepLabels(this.steps),
      activeStep: 0,
      processing: false,
    };
    var currentDate = new Date();
    //console.log("Debug date to toJSON: " + currentDate.toJSON());
    var dateUtil: IDateUtil = new DateFnsDateUtil();
    this.jeExport = {
      postingDate: dateUtil.startOfDay(currentDate),
      ignoreError: true,
    };

    this.handleNext = this.handleNext.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
    this.handleReset = this.handleReset.bind(this);
  }

  isStepOptional(step: number): boolean {
    return false;
  }

  isStepSkipped(step: number): boolean {
    return false;
  }

  async handleNext() {
    this.setState({ processing: true });
    const handleResult = await this.handleCurrentSelection();
    if (!handleResult.success) {
      alert(handleResult.message);
    } else {
      let newSkipped = this.skipped;
      if (this.isStepSkipped(this.state.activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(this.state.activeStep);
        this.skipped = newSkipped;
      }

      const newActiveStep = this.state.activeStep + 1;
      if (newActiveStep === this.state.steps.length) {
        //TODO
        alert("It's all done!");
      } else {
        this.setState({ activeStep: newActiveStep });
      }
    }
    this.setState({ processing: false });
  }

  handleBack() {
    //TODO rollback data?
    const newActiveStep = this.state.activeStep - 1;
    this.setState({ activeStep: newActiveStep });
  }

  handleSkip() {
    if (!this.isStepOptional(this.state.activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    const newActiveStep = this.state.activeStep + 1;
    this.setState({ activeStep: newActiveStep });
    const newSkipped = new Set(this.skipped.values());
    newSkipped.add(this.state.activeStep);
    this.skipped = newSkipped;
  }

  handleReset = () => {
    this.setState({ activeStep: 0 });
  };

  async handleCurrentSelection(): Promise<CreationStepHandledResult> {
    switch (this.steps[this.state.activeStep].key) {
      case "selectPostingDate":
        return await this.handleSelectPostingDate();
      case "reviewRawTransactions":
        return await this.handleReviewRawTransactions();
      case "reviewJes":
        return await this.handleReviewJes();
      case "reviewGroupedJes":
        return await this.handleReviewGroupedJes();
      case "reviewJeExports":
        return { success: true };
      default:
        return {
          success: false,
          message: "Step processing is still under construction",
        };
    }
  }

  async handleSelectPostingDate(): Promise<CreationStepHandledResult> {
    if (!this.jeExport.postingDate) {
      return { success: false, message: "Please select a posting date" };
    } else {
      this.logger.debug("Posting Date selected: ", this.jeExport.postingDate);
      var request = {
        postingDate: this.jeExport.postingDate,
        organizationId: getUserProfileFromCache(this.pageProps.dataStore).defaultOrganization,
      };
      const notificationsBefore = this.pageProps.appUtil.getApp().getNotifications().length;
      var response = await this.httpClient.post<
        GetTessituraTransactionsByPostingDateResponse,
        GetTessituraTransactionsByPostingDateRequest
      >("tessitura/transactions/get-by-posting-date", request);
      if (response && response.result) {
        if (response.result.length === 0) {
          return { success: false, message: "No transactions for selected posting date" };
        } else {
          this.jeExport.transactions = response.result;
          return { success: true };
        }
      } else {
        return {
          success: false,
          message: this.buildServerErrorMessage(
            notificationsBefore,
            "Failed to get transactions by posting date",
          ),
        };
      }
    }
  }

  /**
   * Builds an error message that includes the detailed server error captured by the
   * default http error handler (which appends an error notification with the server's
   * exception message and stack trace).
   */
  private buildServerErrorMessage(notificationsBefore: number, fallbackMessage: string): string {
    const notifications = this.pageProps.appUtil.getApp().getNotifications();
    const latest = notifications.length > notificationsBefore ? notifications[notifications.length - 1] : undefined;
    if (latest && latest.type === "error") {
      if (latest.message) {
        return latest.message + `\n\nPlease check the error details in notification center.`;
      } else {
        const detail = latest.detailMessage ? latest.detailMessage : "";
        return `${detail}\n\nPlease check the error details in notification center.`;
      }
    }
    return fallbackMessage;
  }

  async handleReviewRawTransactions(): Promise<CreationStepHandledResult> {
    if (!this.jeExport.transactions || this.jeExport.transactions.length === 0) {
      return { success: false, message: "No transactions to process" };
    }
    var request: GenerateTransactionJournalEntriesRequest = {
      organizationId: getUserProfileFromCache(this.pageProps.dataStore).defaultOrganization,
      transactions: this.jeExport.transactions,
      ignoreError: this.jeExport.ignoreError ?? false,
    };
    var response = await this.httpClient.post<
      GenerateTransactionJournalEntriesResponse,
      GenerateTransactionJournalEntriesRequest
    >("tessitura/integration/generate-transaction-journal-entries", request);
    if (response && response.entries) {
      this.jeExport.transactionJournalEntries = response.entries;
      this.jeExport.transactionErrors = response.transactionErrors;
      return { success: true };
    } else {
      return { success: false, message: "Failed to generate transaction journal entries" };
    }
  }

  async handleReviewJes(): Promise<CreationStepHandledResult> {
    if (!this.jeExport.transactionJournalEntries || this.jeExport.transactionJournalEntries.length === 0) {
      return { success: false, message: "No transaction journal entries to group" };
    }
    var request: GroupJournalEntriesRequest = {
      organizationId: getUserProfileFromCache(this.pageProps.dataStore).defaultOrganization,
      entries: this.jeExport.transactionJournalEntries,
    };
    var response = await this.httpClient.post<
      GroupJournalEntriesResponse,
      GroupJournalEntriesRequest
    >("tessitura/integration/group-journal-entries", request);
    if (response && response.entries) {
      this.jeExport.groupedJournalEntries = response.entries;
      this.jeExport.groupingErrors = response.groupingErrors;
      return { success: true };
    } else {
      return { success: false, message: "Failed to group journal entries" };
    }
  }

  async handleReviewGroupedJes(): Promise<CreationStepHandledResult> {
    if (!this.jeExport.groupedJournalEntries || this.jeExport.groupedJournalEntries.length === 0) {
      return { success: false, message: "No grouped journal entries to export" };
    }
    var request: GetJournalEntryExportResultsRequest = {
      organizationId: getUserProfileFromCache(this.pageProps.dataStore).defaultOrganization,
      journalEntries: this.jeExport.groupedJournalEntries,
    };
    var response = await this.httpClient.post<
      GetJournalEntryExportResponse,
      GetJournalEntryExportResultsRequest
    >("tessitura/integration/get-journal-entry-export-results", request);
    if (response && response.entries) {
      this.jeExport.exports = response.entries.map((item) => {
        var ret = item.fields;
        ret.amount = item.amount;
        return { export: ret, sources: item.sources };
      });
      return { success: true };
    } else {
      return { success: false, message: "Failed to get journal entry export results" };
    }
  }

  getStepContent(): ReactNode {
    switch (this.steps[this.state.activeStep].key) {
      case "selectPostingDate":
        return <SelectPostingDate pageProps={this.pageProps} jeExport={this.jeExport} />;
      case "reviewRawTransactions":
        return <ReviewRawTransactions pageProps={this.pageProps} jeExport={this.jeExport} />;
      case "reviewJes":
        return <ReviewTransactionLevelJournalEntries pageProps={this.pageProps} jeExport={this.jeExport} />;
      case "reviewGroupedJes":
        return <ReviewGroupedJournalEntries pageProps={this.pageProps} jeExport={this.jeExport} />;
      case "reviewJeExports":
        return <ReviewJournalEntryExports pageProps={this.pageProps} jeExport={this.jeExport} />;
      case "confirmation":
        return <ReviewExportConfirmation pageProps={this.pageProps} jeExport={this.jeExport} />;
      default:
        return <UnderConstructionComponent />;
    }
  }

  render(): ReactNode {
    return (
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={this.state.activeStep}>
          {this.state.steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
            } = {};
            if (this.isStepOptional(index)) {
              labelProps.optional = (
                <Typography variant="caption">
                  {this.pageProps.appUtil.getLocaleString("stepLabelOptional")}
                </Typography>
              );
            }
            if (this.isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>
                  {this.pageProps.appUtil.getLocaleString(label)}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {this.state.activeStep === this.state.steps.length ? (
          <React.Fragment>
            <Typography sx={{ mt: 2, mb: 1 }}>Last Step</Typography>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Box sx={{ flex: "1 1 auto" }} />
              <Button
                onClick={() => {
                  alert("TBD");
                }}
              >
                ActionAfterLastStep
              </Button>
              <Button onClick={this.handleReset}>Another Exporting</Button>
            </Box>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Box sx={{ width: "100%" }}>
              {this.getStepContent()}
              <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={this.state.processing}
                showIndicator
                shading
                showPane
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Button
                color="inherit"
                disabled={this.state.activeStep === 0}
                onClick={this.handleBack}
                sx={{ mr: 1 }}
              >
                {this.pageProps.appUtil.getLocaleString("stepActionBack")}
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {this.isStepOptional(this.state.activeStep) && (
                <Button color="inherit" onClick={this.handleSkip} sx={{ mr: 1 }}>
                  Skip
                </Button>
              )}
              {this.state.activeStep < this.state.steps.length - 1 ? (
                <Button onClick={this.handleNext}>
                  {this.pageProps.appUtil.getLocaleString("stepActionNext")}
                </Button>
              ) : (
                <Button onClick={this.handleReset}>
                  Start Over
                </Button>
              )}
            </Box>
          </React.Fragment>
        )}
      </Box>
    );
  }
}

const withWrapper = (WithWrapperForm: any) => (props: PageProps) => {
  const navigate = useNavigate();
  return <WithWrapperForm {...props} {...{ navigate }} />;
};

export default withWrapper(JeExportByPostingDate);
